import { Container, Graphics, Text, TextStyle, Point } from 'pixi.js';
import { RoomCategory, RoomDefinition } from '@gameplay/management/rooms/RoomTypes';

export interface RoomUICallbacks {
  onRoomCategoryChanged: (roomId: string, category: RoomCategory) => void;
  onServiceAnchorPlaced: (category: RoomCategory, position: Point) => void;
  onRoomSelected: (roomId: string | null) => void;
  onEnterCategorizationMode: (category: RoomCategory) => void;
  onExitCategorizationMode: () => void;
}

export class RoomUI extends Container {
  private panel!: Graphics;
  private titleText!: Text;
  private categoryButtons: Map<RoomCategory, CategoryButton> = new Map();
  private selectedRoom: RoomDefinition | null = null;
  private selectedCategory: RoomCategory | null = null;
  private callbacks: RoomUICallbacks;
  
  private readonly PANEL_WIDTH = 280;
  private readonly PANEL_HEIGHT = 400;
  private readonly BUTTON_WIDTH = 120;
  private readonly BUTTON_HEIGHT = 32;

  constructor(callbacks: RoomUICallbacks) {
    super();
    this.callbacks = callbacks;
    this.createPanel();
    this.createCategoryButtons();
    this.visible = false;
  }

  private createPanel(): void {
    this.panel = new Graphics();
    this.panel.beginFill(0x2a2a2a, 0.9);
    this.panel.lineStyle(2, 0x4a4a4a, 1);
    this.panel.drawRoundedRect(0, 0, this.PANEL_WIDTH, this.PANEL_HEIGHT, 8);
    this.panel.endFill();
    this.addChild(this.panel);

    const titleStyle = new TextStyle({
      fontFamily: 'Arial',
      fontSize: 18,
      fill: 0xffffff,
      fontWeight: 'bold'
    });

    this.titleText = new Text('Room Manager', titleStyle);
    this.titleText.position.set(16, 16);
    this.addChild(this.titleText);
  }

  private createCategoryButtons(): void {
    const categories: Array<{category: RoomCategory, label: string, color: number}> = [
      { category: 'bedroom', label: 'Bedroom', color: 0x4A90E2 },
      { category: 'bathroom', label: 'Bathroom', color: 0x7ED321 },
      { category: 'dining', label: 'Dining Hall', color: 0xF5A623 },
      { category: 'medical', label: 'Medical', color: 0xD0021B },
      { category: 'recreation', label: 'Recreation', color: 0xB8E986 },
      { category: 'utility', label: 'Utility', color: 0x50E3C2 },
      { category: 'unspecified', label: 'Clear', color: 0x666666 }
    ];

    let yOffset = 60;
    const buttonSpacing = this.BUTTON_HEIGHT + 8;

    categories.forEach((config, index) => {
      const button = new CategoryButton(
        config.category,
        config.label,
        config.color,
        this.BUTTON_WIDTH,
        this.BUTTON_HEIGHT,
        (category) => this.onCategoryButtonClicked(category)
      );

      const xOffset = index % 2 === 0 ? 16 : 16 + this.BUTTON_WIDTH + 8;
      if (index % 2 === 0 && index > 0) {
        yOffset += buttonSpacing;
      }

      button.position.set(xOffset, yOffset);
      this.addChild(button);
      this.categoryButtons.set(config.category, button);
    });
  }

  private onCategoryButtonClicked(category: RoomCategory): void {
    if (this.selectedRoom) {
      // Apply category to selected room
      this.callbacks.onRoomCategoryChanged(this.selectedRoom.id, category);
      this.updateSelectedCategory(category);
    } else {
      // Check if we're clicking the same button that's already selected (toggle off)
      if (this.selectedCategory === category) {
        this.exitCategorizationMode();
      } else {
        // Enter category placement mode - user can now click rooms to apply this category
        this.selectedCategory = category;
        this.updateButtonStates();
        this.callbacks.onRoomSelected(null); // Clear any existing selection
        this.callbacks.onEnterCategorizationMode(category);
        console.log(`🏠 Room categorization mode activated. Click on rooms to assign as ${category}.`);
      }
    }
  }

  public updateSelectedRoom(room: RoomDefinition | null): void {
    this.selectedRoom = room;
    this.updateDisplay();
  }

  public updateSelectedCategory(category: RoomCategory): void {
    this.selectedCategory = category;
    if (this.selectedRoom) {
      this.selectedRoom.category = category;
    }
    this.updateButtonStates();
  }

  private updateDisplay(): void {
    if (this.selectedRoom) {
      this.titleText.text = `Room: ${this.selectedRoom.category} (${this.selectedRoom.tiles.length} tiles)`;
    } else {
      this.titleText.text = 'Room Manager';
    }
    this.updateButtonStates();
  }

  private updateButtonStates(): void {
    this.categoryButtons.forEach((button, category) => {
      const isSelected = this.selectedRoom?.category === category || this.selectedCategory === category;
      button.setSelected(isSelected);
    });
  }

  public show(): void {
    this.visible = true;
  }

  public hide(): void {
    this.visible = false;
  }

  public toggle(): void {
    this.visible = !this.visible;
  }

  public exitCategorizationMode(): void {
    this.selectedCategory = null;
    this.updateButtonStates();
    this.callbacks.onExitCategorizationMode();
    console.log('🏠 Exited room categorization mode');
  }
}

class CategoryButton extends Container {
  private background: Graphics;
  private labelText: Text;
  private category: RoomCategory;
  private color: number;
  private isSelected: boolean = false;
  private onClick: (category: RoomCategory) => void;

  constructor(
    category: RoomCategory,
    label: string,
    color: number,
    width: number,
    height: number,
    onClick: (category: RoomCategory) => void
  ) {
    super();
    this.category = category;
    this.color = color;
    this.onClick = onClick;

    this.background = new Graphics();
    this.addChild(this.background);

    const textStyle = new TextStyle({
      fontFamily: 'Arial',
      fontSize: 12,
      fill: 0xffffff,
      align: 'center'
    });

    this.labelText = new Text(label, textStyle);
    this.labelText.anchor.set(0.5, 0.5);
    this.labelText.position.set(width / 2, height / 2);
    this.addChild(this.labelText);

    this.drawButton(width, height, false);
    this.setupInteraction();
  }

  private drawButton(width: number, height: number, selected: boolean): void {
    this.background.clear();
    
    if (selected) {
      this.background.beginFill(this.color, 0.8);
      this.background.lineStyle(2, 0xffffff, 1);
    } else {
      this.background.beginFill(this.color, 0.6);
      this.background.lineStyle(1, this.color, 1);
    }
    
    this.background.drawRoundedRect(0, 0, width, height, 4);
    this.background.endFill();
  }

  private setupInteraction(): void {
    this.eventMode = 'static';
    this.cursor = 'pointer';

    this.on('pointerdown', () => {
      this.onClick(this.category);
    });

    this.on('pointerover', () => {
      this.background.tint = 0xdddddd;
    });

    this.on('pointerout', () => {
      this.background.tint = 0xffffff;
    });
  }

  public setSelected(selected: boolean): void {
    this.isSelected = selected;
    this.drawButton(120, 32, selected); // Hardcoded for now, could be parameterized
  }
}